import { addDays, differenceInDays } from "date-fns"
import { max, maxBy, mean, sum } from "lodash"

import { useRouter } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"

import { roundToColliSize } from "~/components/purchaseOrder/CreateOrUpdatePurchaseOrder"
import { OverlapAlertPurchaseOrderCreate } from "~/components/purchaseOrder/OverlapAlertPurchaseOrderCreate"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/reusable/Accordion"
import { Card, CardContent } from "~/components/reusable/Card"
import { useCreatePurchaseOrder, useSuggestedPurchaseOrder } from "~/hooks/usePurchaseOrders"
import { trackEvent } from "~/hooks/useTrackingService"
import { formatDate, startOfTodayUTC } from "~/utils/datetime"
import { filterNotUndefinedOrNull, largest, uniq } from "~/utils/tsUtils"

import { AlertWarning } from "~/components/reusable/Alert"
import { Button } from "../../reusable/Button"
import DatePicker from "~/components/reusable/DatePicker"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import { Input } from "../../reusable/Input"
import { usePrimaryWarehouses } from "~/hooks/useWarehouses"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { WarehouseSelector } from "~/components/purchaseOrder/selectors/WarehouseSelector"
import { VendorSelector } from "~/components/purchaseOrder/selectors/VendorSelector"
import { toast } from "react-toastify"
import { Checkbox } from "~/components/reusable/Checkbox"
import { Label } from "~/components/reusable/Label"
import AdvancedDemandMapping from "./AdvancedDemandMapping"
import { createPortal } from "react-dom"

type PurchaseOrderInput = {
  name: string
  expectedArrivalDate: Date
  nextOrderArrivalDate: Date
  vendorId: string
}

type Props = {
  variants: IVariantSummary[]
  selectableVendors: { id: string; name: string }[]
  preSelectedVendorId?: string
  preSelectedWarehouseId?: string
  start?: Date
  end?: Date
  onCancel?: () => void
  collectionIds?: string[]
  /** Overrides the vendor name in the auto-generated PO name, e.g. product name from the variant page */
  nameContext?: string
}

export function CreatePurchaseOrder({
  variants,
  selectableVendors,
  preSelectedVendorId,
  preSelectedWarehouseId,
  start,
  end,
  onCancel,
  collectionIds,
  nameContext,
}: Props) {
  const router = useRouter()
  const warehouses = usePrimaryWarehouses() // can we make purchase orders on non-primary warehouses?
  const [demandMap, setDemandMap] = useState<Record<string, string[]>>({})
  const preferredVendorIds = uniq(
    filterNotUndefinedOrNull(variants.map((v) => v.settings.preferredVendorId)),
  )

  const selectedSkus = variants.flatMap((v) =>
    v.skuIds.map((skuId) => ({
      skuId,
      colliSize: v.settings.colliSize,
      totalLeadTimeDays: v.settings.totalLeadTimeDays,
      purchaseIntervalDays: v.settings.purchaseIntervalDays,
    })),
  )

  const defaultWarehouseIds = useMemo(() => {
    if (preSelectedWarehouseId) return [preSelectedWarehouseId]

    const warehouseId = maxBy(warehouses, (wh) =>
      sum(
        variants.flatMap((v) => v.stockOutInfoWarehouse[wh.id]?.map((s) => s.suggestedOrder ?? 0)),
      ),
    )?.id

    return warehouseId ? [warehouseId] : []
  }, [variants.length, preSelectedWarehouseId])

  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>(defaultWarehouseIds)
  const [vendorId, setVendorId] = useState<string>(
    preSelectedVendorId ??
      preferredVendorIds.find((id) => selectableVendors.some((v) => v.id === id)) ??
      selectableVendors[0]?.id ??
      "",
  )

  useEffect(() => {
    if (selectedWarehouseIds.length === 0 && warehouses?.length) {
      setSelectedWarehouseIds(warehouses[0]!.id ? [warehouses[0]!.id] : [])
    }
  }, [warehouses?.length])

  const warehouseQuantity = (warehouseId: string) => {
    return sum(
      suggestedOrders
        ?.filter((x) => x.warehouseId === warehouseId)
        .map((x) => Math.ceil(Math.max(x.rawQuantity, 0))) ?? [],
    )
  }

  const [POInput, setPOInput] = useState<PurchaseOrderInput>({
    name: "",
    expectedArrivalDate:
      start ??
      addDays(startOfTodayUTC(), max(selectedSkus.map((sku) => sku.totalLeadTimeDays)) ?? 0),
    nextOrderArrivalDate:
      end ??
      addDays(
        startOfTodayUTC(),
        (max(selectedSkus.map((sku) => sku.totalLeadTimeDays)) ?? 0) +
          (Math.ceil(mean(selectedSkus.map((sku) => sku.purchaseIntervalDays))) || 7),
      ),
    vendorId,
  })

  const colliSizes = useMemo(() => {
    const colliSizeMap: Map<string, number> = new Map()
    selectedSkus.forEach((sku) => {
      if (sku.colliSize) {
        colliSizeMap.set(sku.skuId, sku.colliSize)
      }
    })
    return colliSizeMap
  }, [selectedSkus])

  const [hasEditedName, setHasEditedName] = useState(false)
  const [onlySuggestedVariants, setOnlySuggestedVariants] = useState(false)
  const [datesModified, setDatesModified] = useState(false)

  const createPurchaseOrder = useCreatePurchaseOrder()
  const suggestedOrders = useSuggestedPurchaseOrder(
    selectedSkus.map((sku) => sku.skuId),
    warehouses.map((x) => x.id),
    datesModified ? POInput.expectedArrivalDate : undefined,
    datesModified ? POInput.nextOrderArrivalDate : undefined,
  )

  const skuToVariantMap = useMemo(() => {
    const map = new Map<string, string>()
    variants.forEach((variant) => {
      variant.skuIds.forEach((skuId) => {
        map.set(skuId, variant.variantId)
      })
    })
    return map
  }, [variants])

  const variantsWithSuggestions = useMemo(() => {
    const variantSet = new Set<string>()
    suggestedOrders?.forEach((item) => {
      if (item.quantity > 0 && item.warehouseId === selectedWarehouseIds[0]) {
        const variantId = skuToVariantMap.get(item.skuId)
        if (variantId) {
          variantSet.add(variantId)
        }
      }
    })
    return variantSet
  }, [suggestedOrders, skuToVariantMap])

  useEffect(() => {
    let name = POInput.name
    if (!hasEditedName && POInput.expectedArrivalDate) {
      const vendor = selectableVendors.find((v) => v.id === vendorId)
      const context = nameContext ?? vendor?.name
      if (context) {
        name = `${context} - ${formatDate(POInput.expectedArrivalDate)}`
      }
    }
    setPOInput((prevState) => ({ ...prevState, vendorId, name }))
  }, [vendorId, POInput.expectedArrivalDate, nameContext])

  const onSubmit = () => {
    if (suggestedOrders === undefined || !selectedWarehouseIds[0]) {
      return
    }

    // Assume demandMap has only one entry: { mainWarehouse: [sub1, sub2, ...] }
    const [mainWarehouseId, subWarehouseIds] = Object.entries(demandMap)[0] ?? [undefined, []]
    const warehouseAliasSet = new Set(subWarehouseIds)

    const demandMapWarehouses = demandMap[selectedWarehouseIds[0]] || []

    const items = suggestedOrders
      .filter((item) =>
        [...selectedWarehouseIds, ...demandMapWarehouses].includes(item.warehouseId),
      )
      .filter((item) => {
        if (onlySuggestedVariants) {
          const variantId = skuToVariantMap.get(item.skuId)
          return variantId && variantsWithSuggestions.has(variantId)
        } else {
          return true
        }
      })

    // Step 2: Remap + combine items by skuId (merged under mainWarehouseId)
    const itemMap = new Map<string, (typeof items)[0]>()

    items.forEach((item) => {
      const mergedWarehouseId = warehouseAliasSet.has(item.warehouseId)
        ? mainWarehouseId
        : item.warehouseId

      const key = `${mergedWarehouseId}_${item.skuId}`

      if (!itemMap.has(key)) {
        itemMap.set(key, {
          ...item,
          warehouseId: mergedWarehouseId!,
        })
      } else {
        const existing = itemMap.get(key)!
        itemMap.set(key, {
          ...existing,
          quantity: existing.quantity + item.quantity,
          rawQuantity: existing.rawQuantity + item.rawQuantity,
        })
      }
    })

    const mergedItems = Array.from(itemMap.values())

    // Step 3: Validate only one warehouse remains
    const uniqueWarehouseIds = new Set(mergedItems.map((item) => item.warehouseId))
    if (uniqueWarehouseIds.size > 1) {
      toast.error("Cannot create purchase order with items from multiple warehouses")
      console.error(
        "Attempted to create PO with multiple warehouses:",
        Array.from(uniqueWarehouseIds),
      )
      return
    }

    // Fallback for variants with no forecast data (absent from ForecastVariantCompressed):
    // create the PO with quantity 0 so the user can set quantities manually in the PO editor.
    const poItems =
      mergedItems.length > 0 || onlySuggestedVariants
        ? items.map((item) => ({
            skuId: item.skuId,
            warehouseId: item.warehouseId,
            quantity: roundToColliSize(
              Math.ceil(largest(item.rawQuantity, 0) ?? 0),
              colliSizes.get(item.skuId) ?? 1,
            ),
          }))
        : selectedSkus.map((sku) => ({
            skuId: sku.skuId,
            warehouseId: selectedWarehouseIds[0]!,
            quantity: 0,
          }))

    if (poItems.length === 0) {
      toast.warning("No items selected for purchase order")
      return
    }

    createPurchaseOrder.mutate(
      {
        ...POInput,
        items: poItems,
        purchasePeriodEndDate: POInput.nextOrderArrivalDate,
        addedDemandWarehouseIds: demandMapWarehouses,
        collectionIds: collectionIds ?? [],
      },
      {
        onSuccess: (data) => {
          trackEvent("Purchase Order Created")
          router.navigate({
            to: `/procurement/purchase-orders/update/$purchaseOrderId`,
            params: { purchaseOrderId: data.id.toString() },
          })
        },
      },
    )
  }

  const dialogFooterDomNode = document.getElementById("purchaseOrderDialogFooter")
  const endOfPurchasePeriodMinDate = addDays(POInput.expectedArrivalDate, 1)

  return (
    <div className="flex flex-col gap-4">
      <WarehouseSelector
        warehouses={warehouses}
        warehouseIds={selectedWarehouseIds}
        setWarehouseIds={setSelectedWarehouseIds}
        warehouseQuantity={warehouseQuantity}
      />

      <AdvancedDemandMapping warehouseIds={selectedWarehouseIds} onDemandMapChange={setDemandMap} />

      <VendorSelector
        selectableVendors={selectableVendors}
        vendorId={vendorId}
        setVendorId={setVendorId}
        preferredVendorIds={preferredVendorIds}
      />

      <div className="flex flex-col gap-4">
        <Input
          label="Name"
          className="h-11"
          containerClassName="w-full"
          value={POInput.name}
          onChange={(e) => {
            setHasEditedName(true)
            setPOInput({ ...POInput, name: e.target.value })
          }}
        />
        <Card>
          <CardContent className="py-0">
            <Accordion type="single" collapsible>
              <AccordionItem className="border-0" value="item-1">
                <AccordionTrigger>Settings</AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-row justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-1">
                        <Label>Expected arrival date</Label>
                        <InfoTooltip title="Changing the arrival date will recalculate suggested quantities for this PO." />
                      </div>
                      <DatePicker
                        value={POInput.expectedArrivalDate}
                        onChange={(date) => {
                          if (!date) return
                          setDatesModified(true)
                          setPOInput({
                            ...POInput,
                            expectedArrivalDate: date,
                            nextOrderArrivalDate: addDays(
                              POInput.nextOrderArrivalDate,
                              differenceInDays(date, POInput.expectedArrivalDate),
                            ),
                          })
                        }}
                        minDate={addDays(startOfTodayUTC(), 1)}
                      />
                    </div>
                    <div>
                      <div className="flex flex-row items-center gap-1">
                        <Label>End of purchase period</Label>
                        <InfoTooltip title="Length of the period you want your purchase order to cover." />
                      </div>
                      <DatePicker
                        value={POInput.nextOrderArrivalDate}
                        onChange={(date) => {
                          if (!date) return
                          setDatesModified(true)
                          setPOInput({ ...POInput, nextOrderArrivalDate: date })
                        }}
                        minDate={endOfPurchasePeriodMinDate}
                      />
                    </div>
                  </div>
                  {datesModified && (
                    <AlertWarning title="Quantities recalculated">
                      The suggested quantities reflect the updated arrival date, not the original
                      order suggestions.
                    </AlertWarning>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <OverlapAlertPurchaseOrderCreate
          numberOfVariantsWithSuggestedOrder={variantsWithSuggestions.size}
          variantsLength={variants.length}
        />

        {!!dialogFooterDomNode &&
          createPortal(
            <div className="flex flex-row justify-between items-center w-full">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="only-suggested-variants-checkbox"
                  checked={onlySuggestedVariants}
                  onCheckedChange={() => setOnlySuggestedVariants(!onlySuggestedVariants)}
                />
                <Label className="cursor-pointer" htmlFor="only-suggested-variants-checkbox">
                  Only include variants with recommendations
                </Label>
              </div>
              <div className="flex gap-2">
                <Button variant="transparent" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  requires="edit"
                  disabled={
                    !POInput.name ||
                    !POInput.expectedArrivalDate ||
                    (onlySuggestedVariants && !suggestedOrders?.some((s) => s.quantity > 0))
                  }
                  loading={createPurchaseOrder.isPending}
                  onClick={() => onSubmit()}
                >
                  Create Order
                </Button>
              </div>
            </div>,
            dialogFooterDomNode,
          )}
      </div>
    </div>
  )
}
